/**
 * CopyField: a read-only value (link, key, code) with a Copy button and spoken "Copied" feedback (cockatrice-ui
 * `src/ui/CopyField.jsx`, ported to hyperscript; PLAN.md §0).
 */
import './css/copy-field.css';
import { createSignal, createUniqueId, mergeProps, onCleanup, splitProps } from 'solid-js';
import Icon from './Icon.ts';
import { component, el, when } from './solid.ts';
import type { Attributes, Child, On } from './solid.ts';
import { uiText } from './text.ts';

export interface CopyFieldProps extends Attributes {
  /** The value to show and copy. */
  readonly value: string;
  /** Label. Default "Link". */
  readonly label?: string | undefined;
  /** Visually hide the label (kept for screen readers). */
  readonly hideLabel?: boolean | undefined;
  /** Button text. Default "Copy". */
  readonly copyLabel?: string | undefined;
  /** Use the mono font. Default true. */
  readonly mono?: boolean | undefined;
  /**
   * Copies the value in place of the browser's clipboard (an app's own copier): resolves false when it couldn't, and the
   * field then says nothing of its own (the caller says why, `onCopy` told `false`).
   */
  readonly copy?: ((value: string) => Promise<boolean>) | undefined;
  /** Called after copying; `copied` is false only when `copy` couldn't. */
  readonly onCopy?: ((value: string, copied: boolean) => void) | undefined;
  /** Ref to the <input> (to move the focus to it, say). */
  readonly inputRef?: ((el: HTMLInputElement) => void) | undefined;
  /** Extra class. */
  readonly class?: string | undefined;
}

const filled = (text: string | undefined): text is string => text !== undefined && text !== '';

export default function CopyField(props: CopyFieldProps): Child {
  const [local, rest] = splitProps(props, ['value', 'label', 'hideLabel', 'copyLabel', 'copy', 'onCopy', 'inputRef', 'mono', 'class']);
  const id = createUniqueId();
  const [copied, setCopied] = createSignal(false);
  let timer: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => {
    clearTimeout(timer);
  });
  const copy = async (): Promise<void> => {
    const value = local.value;
    const own = local.copy;
    let done = true;
    if (own !== undefined) done = await own(value);
    else {
      try {
        // The handoff's `navigator.clipboard?.writeText`: the clipboard is missing outside a secure context.
        if ('clipboard' in navigator) await navigator.clipboard.writeText(value);
      } catch {
        /* clipboard blocked: the field stays selectable */
      }
    }
    local.onCopy?.(value, done);
    clearTimeout(timer);
    setCopied(done);
    if (done) timer = setTimeout(() => setCopied(false), 2000);
  };
  // `{...rest}` comes last in the handoff's JSX, so what the caller passes wins over the field's own class.
  return el(
    'div',
    mergeProps({ class: () => `ui-copy-field${filled(local.class) ? ` ${local.class}` : ''}` }, rest),
    el(
      'label',
      { class: () => `ui-copy-field__label${local.hideLabel === true ? ' is-hidden' : ''}`, for: id },
      () => (filled(local.label) ? local.label : uiText()('ui.copy-field.label')),
    ),
    el(
      'div',
      { class: 'ui-copy-field__row' },
      el('input', {
        ref: (node: HTMLInputElement) => {
          local.inputRef?.(node);
        },
        id,
        class: () => `ui-copy-field__input${local.mono === false ? '' : ' is-mono'}`,
        type: 'text',
        readonly: true,
        value: () => local.value,
        onFocus: (e: On<FocusEvent, HTMLInputElement>) => {
          e.currentTarget.select();
        },
      }),
      el(
        'button',
        {
          type: 'button',
          class: 'ui-copy-field__btn',
          onClick: () => {
            void copy();
          },
        },
        component(Icon, {
          get name() {
            return copied() ? 'check' : 'copy';
          },
          size: 'sm',
        }),
        () => (copied() ? uiText()('ui.copy-field.copied') : filled(local.copyLabel) ? local.copyLabel : uiText()('ui.copy-field.copy')),
      ),
    ),
    el(
      'span',
      { class: 'ui-copy-field__status', role: 'status', 'aria-live': 'polite' },
      when(
        () => copied(),
        () => () => uiText()('ui.copy-field.status'),
      ),
    ),
  );
}
